/**
 * Type Guards and Type Utilities
 *
 * Type-safe helpers for narrowing and validating types
 * Eliminates unnecessary type conversions and improves type safety
 */

import type { PickemQuestion } from '@low6-pickem/api-types';

// =============================================================================
// Question Type Guards
// =============================================================================

/**
 * Type guard to check if a question is a team pick question
 */
export function isTeamQuestion(question: PickemQuestion): boolean {
  return question.question_type === 'team' || question.question_type === 'TEAM_PICK';
}

/**
 * Type guard to check if a question is a player pick question
 */
export function isPlayerQuestion(question: PickemQuestion): boolean {
  return question.question_type === 'player';
}

/**
 * Type guard to check if a question is a prop question
 */
export function isPropQuestion(question: PickemQuestion): boolean {
  return question.question_type === 'prop';
}

/**
 * Type guard to check if a question is an over/under question
 */
export function isOverUnderQuestion(question: PickemQuestion): boolean {
  return question.question_type === 'over_under';
}

/**
 * Type guard to check if a question is a tiebreaker question
 */
export function isTiebreakerQuestion(question: PickemQuestion): boolean {
  return question.is_tiebreak === true || question.question_type === 'tiebreaker';
}

// =============================================================================
// ID Type Utilities
// =============================================================================

/**
 * Safely convert a question_id to string
 * Handles both string and number types from API
 */
export function toQuestionId(id: string | number | undefined | null): string {
  if (id === undefined || id === null) return '';
  return String(id);
}

/**
 * Safely convert an option_id to string
 * Handles both string and number types from API
 */
export function toOptionId(id: string | number | undefined | null): string {
  if (id === undefined || id === null) return '';
  return String(id);
}

/**
 * Type guard to check if a value is a valid ID (non-empty string)
 */
export function isValidId(id: unknown): id is string {
  return typeof id === 'string' && id.length > 0;
}

// =============================================================================
// Answer Type Guards
// =============================================================================

/**
 * Type guard to check if an answer is a yes/no answer
 */
export function isYesNoAnswer(answer: unknown): answer is 'yes' | 'no' {
  return answer === 'yes' || answer === 'no';
}

/**
 * Type guard to check if an answer is a team/option ID
 */
export function isTeamAnswer(answer: unknown): answer is string {
  return typeof answer === 'string' && answer !== 'yes' && answer !== 'no';
}

// =============================================================================
// Value Assertions
// =============================================================================

/**
 * Assert that a value is defined (not null or undefined)
 * Throws an error if the value is null or undefined
 */
export function assertDefined<T>(
  value: T | null | undefined,
  errorMessage: string = 'Value is null or undefined'
): asserts value is T {
  if (value === null || value === undefined) {
    throw new Error(errorMessage);
  }
}

/**
 * Assert that a value is a non-empty array
 */
export function assertNonEmptyArray<T>(
  value: T[],
  errorMessage: string = 'Array is empty'
): asserts value is [T, ...T[]] {
  if (value.length === 0) {
    throw new Error(errorMessage);
  }
}
